import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { GradientTitle } from '../components/ui/typography/GradientTitle';
import type { CognitiveBias } from '../types';
import { Helmet } from 'react-helmet-async';
import BiasNetworkGraph from '../components/visualizations/BiasNetworkGraph';
import { useState, useEffect } from 'react';

interface CategoryPageProps {
  biases: CognitiveBias[];
}

export const CategoryPage: React.FC<CategoryPageProps> = ({ biases }) => {
  const { categoryId } = useParams();
  const [isNetworkVisible, setIsNetworkVisible] = useState(false);
  const [showNetworkGraph, setShowNetworkGraph] = useState(true);
  const [isNetworkLoading, setIsNetworkLoading] = useState(true);
  
  // Get biases for this category and sort alphabetically
  const categoryBiases = biases
    .filter(bias => bias.category.toLowerCase() === categoryId?.toLowerCase())
    .sort((a, b) => a.name.localeCompare(b.name));

  // Get category title (convert from URL format)
  const categoryTitle = categoryId
    ?.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const categoryDescriptions: Record<string, string> = {
    'memory': 'Discover how cognitive biases influence our memory. Learn about cognitive distortions that affect recall and how past experiences shape current decisions.',
    'social': 'Discover biases that influence our interactions and social judgments through real-world examples.',
    'decision-making': 'Learn about cognitive biases that impact our choices and judgment through real-world examples.',
    'perceptual': 'Explore cognitive biases that filter how we see the world around us. Learn how our brain processes information selectively and affects our thinking.',
    'reasoning': 'Understand cognitive biases that affect logical thinking and problem-solving. Discover how these shortcuts impact analysis and decision-making processes.'
  };

  // Check if screen is desktop
  useEffect(() => {
    const checkScreenSize = () => {
      setIsNetworkVisible(window.innerWidth >= 1024); // lg breakpoint
    };
    
    // Initial check
    checkScreenSize();
    
    // Add resize listener
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  
  // Reset loading state when graph is toggled
  useEffect(() => {
    if (showNetworkGraph) {
      setIsNetworkLoading(true);
      // Add a small timeout to simulate loading and ensure the component has mounted
      const timer = setTimeout(() => {
        setIsNetworkLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showNetworkGraph]);

  if (categoryBiases.length === 0) {
    return (
      <div className="container mx-auto px-6 py-12">
        <div className="text-center text-gray-600">No biases found in this category.</div>
      </div>
    );
  }

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{categoryTitle} Biases | Cognitive Bias Lab</title>
        <meta 
          name="description" 
          content={categoryDescriptions[categoryId?.toLowerCase() || ''] || 
            `Explore ${categoryTitle} biases and learn how they affect your thinking and decision-making.`} 
        />
        <link 
          rel="canonical" 
          href={`https://www.cognitivebiaslab.com/category/${categoryId}/`}
          key="canonical"
          data-source="page-component"
        />
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content={`${categoryTitle} Biases | Cognitive Bias Lab`} />
        <meta 
          property="og:description" 
          content={categoryDescriptions[categoryId?.toLowerCase() || ''] || 
            `Explore ${categoryTitle} biases and learn how they affect your thinking and decision-making.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.cognitivebiaslab.com/category/${categoryId}/`} />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${categoryTitle} Biases | Cognitive Bias Lab`} />
        <meta 
          name="twitter:description" 
          content={categoryDescriptions[categoryId?.toLowerCase() || ''] || 
            `Explore ${categoryTitle} biases and learn how they affect your thinking and decision-making.`}
        />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>

      <div className="min-h-screen bg-orange-50 py-12">
        <div className="container mx-auto px-4">
          <Link 
            to="/learn/" 
            className="inline-flex items-center text-gray-600 hover:text-orange-600 mb-8 group"
          >
            <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
            Back to Categories
          </Link>

          <div className="text-center mb-12">
            <GradientTitle className="text-3xl md:text-4xl mb-4">
              {categoryTitle} Biases
            </GradientTitle>
            <p className="text-lg text-gray-600 mx-auto max-w-2xl">
              {categoryDescriptions[categoryId?.toLowerCase() || ''] || 
                `Explore ${categoryTitle} biases and learn how they affect your thinking and decision-making.`}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categoryBiases.map((bias) => (
              <Link
                key={bias.id}
                to={`/bias/${bias.id}/`}
                className="group bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl 
                  transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-gray-100
                  hover:border-orange-200 flex flex-col"
              >
                <h2 className="text-xl font-semibold mb-3 text-gray-800">{bias.name}</h2>
                <p className="text-gray-600 line-clamp-3 mb-6 flex-grow">
                  {bias.short_description}
                </p>
                <div className="text-orange-500 font-medium flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                  Learn More <ChevronLeft className="w-4 h-4 rotate-180" />
                </div>
              </Link>
            ))}
          </div>
          
          {/* Network Graph Section - visible only on desktop */}
          {isNetworkVisible && (
            <div className="mt-16">
              <div className="border-t border-gray-200 pt-12">
                <div className="text-center mb-8">
                  <GradientTitle useH1={false} className="text-3xl md:text-4xl mb-4">
                    Visual Graph of {categoryTitle} Biases
                  </GradientTitle>
                  <p className="text-lg text-gray-600 mx-auto max-w-2xl">
                    Explore how these biases connect and influence each other in an interactive visualization
                  </p>
                </div>
                <div className="flex justify-center mb-8">
                  <button
                    onClick={() => setShowNetworkGraph(!showNetworkGraph)}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white rounded-lg transition-all shadow-md flex items-center gap-2"
                  >
                    {showNetworkGraph ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Hide Graph View
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                        Show Graph View
                      </>
                    )}
                  </button>
                </div>
                
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showNetworkGraph ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="rounded-xl p-4">
                    {showNetworkGraph && (
                      <>
                        {isNetworkLoading && (
                          <div className="flex justify-center items-center h-[600px]">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                          </div>
                        )}
                        <div className={isNetworkLoading ? 'hidden' : 'block'}>
                          <BiasNetworkGraph selectedCategory={categoryId || ''} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}; 